import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useResponsive } from '../../hooks/useResponsive';
import { DINING_GROUPS, DINING_BEHAVIORS } from '../../types/filters';
import './DiningBehaviorFilter.css';

interface DiningBehaviorFilterProps {
  disabled?: boolean;
}

const DiningBehaviorFilter: React.FC<DiningBehaviorFilterProps> = ({ disabled = false }) => {
  const { t } = useTranslation();
  const { tempFilters, setDiningBehaviorFilter, applyTempFilters, cancelTempFilters, hasUnappliedChanges } = useFilters();
  const { isLaptop, isiPad, isiPhone } = useResponsive();
  const [showPicker, setShowPicker] = useState(false);

  const diningBehavior = tempFilters.diningBehavior;
  
  
  const isGroupFullySelected = (group: any) => {
    return group.behaviors.every((behavior: string) => diningBehavior.selectedBehaviors.includes(behavior));
  }; 

  const handleBehaviorToggle = (behaviorValue: string) => {
    if (disabled) return; 
    
    const newSelectedBehaviors = diningBehavior.selectedBehaviors.includes(behaviorValue)
      ? diningBehavior.selectedBehaviors.filter(behavior => behavior !== behaviorValue)
      : [...diningBehavior.selectedBehaviors, behaviorValue];

    setDiningBehaviorFilter({
      selectedBehaviors: newSelectedBehaviors,
      allBehaviors: newSelectedBehaviors.length === DINING_BEHAVIORS.length
    });
  };

  const handleAllBehaviorsToggle = () => {
    if (disabled) return; 
    
    if (diningBehavior.allBehaviors) {
      setDiningBehaviorFilter({
        selectedBehaviors: [],
        allBehaviors: false
      });
    } else {
      setDiningBehaviorFilter({
        selectedBehaviors: DINING_BEHAVIORS.map(b => b.value),
        allBehaviors: true
      });
    }
  };

  const getButtonText = () => {
    if (diningBehavior.allBehaviors) {
      return t('diningFilter.buttonText.allServices');
    } else if (diningBehavior.selectedBehaviors.length === 0) {
      return t('diningFilter.buttonText.selectServices');
    } else {
      const selectedLabels = diningBehavior.selectedBehaviors.map(value => {
        const behavior = DINING_BEHAVIORS.find(b => b.value === value);
        return behavior ? behavior.label : value;
      });
      
      if (selectedLabels.length <= 2) {
        return selectedLabels.join(', ');
      } else {
        return t('diningFilter.buttonText.selectedWithMore', {
          selected: selectedLabels.slice(0, 2).join(', '),
          count: selectedLabels.length - 2
        });
      }
    }
  };

  const diningTextStyles = {
    fontSize: isiPhone ? '0.85rem' : isiPad ? '0.9rem' : isLaptop ? '1.05rem' : '1.2rem',
    fontWeight: 700,
    color: 'white',
    letterSpacing: '0.3px',
    flex: 1,
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const
  };

  return (
    <div className={`dining-behavior-filter ${disabled ? 'disabled' : ''}`}>
      <button 
        className={`filter-button dining-behavior-button ${diningBehavior.selectedBehaviors.length > 0 ? 'active' : ''}`}
        onClick={() => !disabled && setShowPicker(!showPicker)} 
        disabled={disabled}
      >
        <span className="dining-icon">🍽️</span>
        <span className="dining-text" style={diningTextStyles}>{getButtonText()}</span>
        <span className="dropdown-arrow">{showPicker ? '▲' : '▼'}</span>
      </button>

      {showPicker && !disabled && ( 
        <div className="dining-picker-dropdown">
          <div className="dining-group-section">
            <div className="dining-group-title">{t('diningFilter.serviceGroups')}</div>
            <div className="dining-group-grid">
              {DINING_GROUPS.map((group) => (
                <button
                  key={group.value}
                  className={`dining-group-button ${isGroupFullySelected(group) ? 'active' : ''}`}
                  onClick={() => {
                    if (isGroupFullySelected(group)) {
                      
                      const otherGroups = DINING_GROUPS.filter(g => g.value !== group.value);
                      const behaviorsInOtherSelectedGroups = otherGroups
                        .filter(g => isGroupFullySelected(g))
                        .flatMap(g => g.behaviors);
                      
                      
                      const behaviorsToRemove = group.behaviors.filter(b => !behaviorsInOtherSelectedGroups.includes(b));
                      const newSelectedBehaviors = diningBehavior.selectedBehaviors.filter(b => !behaviorsToRemove.includes(b));
                      
                      setDiningBehaviorFilter({
                        selectedBehaviors: newSelectedBehaviors,
                        allBehaviors: newSelectedBehaviors.length === DINING_BEHAVIORS.length
                      });
                    } else {
                      
                      const combined = [...diningBehavior.selectedBehaviors, ...group.behaviors];
                      const newSelectedBehaviors = Array.from(new Set(combined));
                      
                      setDiningBehaviorFilter({
                        selectedBehaviors: newSelectedBehaviors,
                        allBehaviors: newSelectedBehaviors.length === DINING_BEHAVIORS.length
                      });
                    }
                  }}
                >
                  <span className="group-icon">{group.icon}</span>
                  <span className="group-label">{group.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="behavior-selection-section">
            <div className="behavior-selection-title">{t('diningFilter.detailedServices')}</div>
            <div className="behavior-buttons">
              {DINING_BEHAVIORS.map((behavior) => (
                <button
                  key={behavior.value}
                  className={`behavior-button ${diningBehavior.selectedBehaviors.includes(behavior.value) ? 'selected' : ''}`}
                  onClick={() => handleBehaviorToggle(behavior.value)}
                >
                  <span className="behavior-icon">{behavior.icon}</span>
                  <span className="behavior-label">{behavior.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="picker-actions">
            <button 
              className="cancel-button"
              onClick={() => {
                cancelTempFilters();
                setShowPicker(false);
              }}
              disabled={!hasUnappliedChanges}
            >
              {t('diningFilter.cancel')}
            </button>
            <button 
              className="apply-button"
              onClick={() => {
                applyTempFilters();
                setShowPicker(false);
              }}
            >
              {t('diningFilter.apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiningBehaviorFilter;